//React
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
//Libraries
import cx from "classnames";
//Components
import {
  Card,
  DocsCodeBlock,
  DocsFlexSectionBlock,
  DocsScreen,
  FlexBox,
  TitleBlock,
  Image,
} from "components";
//Constants
import { docsConstants } from "../../../constants";
//Utils
import { EventManager, EventNames, useIsInViewport } from "utils";
//Styles
import "./styles.scss";
//Assets
import AppleIcon from "assets/icons/apple.svg";
import iOSSdkImg1 from "assets/docs/iOS-1.png";
import iOSSdkImg2 from "assets/docs/iOS-2.png";
import iOSSdkImg3 from "assets/docs/iOS-3.png";
import iOSSdkImg4 from "assets/docs/iOS-4.png";

const DocsHome = () => {
  /* Constants */
  //States
  const [scrolledToElement, setScrolledToElement]: any = useState({
    selectedTab: docsConstants.TITLES.OVERVIEW,
  });

  //Params
  const sdkType: any = useParams();

  //Refs
  const WelcomeSectionRef: any = useRef();
  const isWelcomeSectionInViewport = useIsInViewport(WelcomeSectionRef);
  //iOS Sdk
  const iOSInstallationRef: any = useRef();
  const isiOSInstallationInViewport = useIsInViewport(iOSInstallationRef);
  const iOSUsageRef: any = useRef();
  const isiOSUsageInViewport = useIsInViewport(iOSUsageRef);
  const iOSHowItWorksRef: any = useRef();
  const isiOSHowItWorksInViewport = useIsInViewport(iOSHowItWorksRef);

  //Jsons
  const scrollAccToSpecificElementMap = {
    [docsConstants.TITLES.OVERVIEW]: WelcomeSectionRef,
    [docsConstants.TITLES.IOS.INSTALLATION]: iOSInstallationRef,
    [docsConstants.TITLES.IOS.USAGE]: iOSUsageRef,
    [docsConstants.TITLES.IOS.HOW_IT_WORKS]: iOSHowItWorksRef,
  };

  /* useEffects */
  useEffect(() => {
    if (isWelcomeSectionInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.OVERVIEW,
      });
    } else if (isiOSInstallationInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.IOS.INSTALLATION,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isiOSUsageInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.IOS.USAGE,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    } else if (isiOSHowItWorksInViewport) {
      setScrolledToElement({
        selectedTab: docsConstants.TITLES.IOS.HOW_IT_WORKS,
        selectedParentTab: docsConstants.TITLES.LOGIN_WITH_WHATSAPP,
        selectedGRandParentTab: docsConstants.TITLES.SDK_AND_API_REFERENCE,
      });
    }
  }, [
    isWelcomeSectionInViewport,
    //ios sdk
    isiOSInstallationInViewport,
    isiOSUsageInViewport,
    isiOSHowItWorksInViewport,
  ]);

  /* Functions */
  //Scroll To Specific element
  const scrollToSpecificElement = (selectedTab) => {
    scrollAccToSpecificElementMap[selectedTab]?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  };

  //Render UI
  const WelcomeSection = (
    <div ref={WelcomeSectionRef}>
      <TitleBlock
        title={
          <FlexBox alignCenter className="section-header">
            <Image src={AppleIcon} className="mr-r-10" />
            <p>iOS SDK</p>
          </FlexBox>
        }
        content={
          <p className="fs-16">
            The iOS SDK help you get up and running with OTPless faster by
            avoiding the struggles associated with to handle frontend functions.
          </p>
        }
        titleClass="fs-32 font-heavy"
        className="animated fadeInUp mr-b-60"
      />
    </div>
  );

  const iOSSdk = () => {
    const Installation = (
      <div ref={iOSInstallationRef}>
        <TitleBlock
          title={docsConstants.TITLES.IOS.INSTALLATION}
          content={
            <>
              <p className="font-medium fs-16 mr-b-20">
                Please add both the files as provided by the OTPless team to
                your application’s lib folder .
              </p>
              <a
                href={docsConstants.ENDPOINT.IOS.SDK_LINK}
                download="OTPless-iOS-SDK.zip"
                className="theme-btn dsp-b w-fc clicking-animation mr-b-20 fs-14 text-dec-none br-9 bg-dark-theme font-heavy pd-10"
                onClick={() => {
                  EventManager.send(EventNames.EVENTS_NAME.DOCS.IOS.DOWNLOAD);
                }}
              >
                Download SDK
              </a>
              <ul className="fs-16 dots">
                <li>libOTPLess.a</li>
                <li>OTPLess.swiftmodule Folder</li>
              </ul>
              <Image
                src={iOSSdkImg1}
                className="mr-v-20 w-100 pd-20 br-9 bg-gray"
              />
              <p className="font-medium fs-18 mr-b-20">
                Don’t Drag and Drop files to the lib folder, The better way is
                to copy both files and paste to the lib folder in your target
                app and don’t forget to add files via navigation just as shown
                below:-
              </p>
              <Image
                src={iOSSdkImg2}
                className="mr-v-20 w-100 pd-20 br-9 bg-gray"
              />
              <div className="fs-16">
                <p className="mr-t-20">
                  Select the project’s name in project navigator, then select
                  General, and select your’s application target.
                </p>
                <p className="mr-t-20">
                  The section Linked Frameworks and Libraries has to contain a
                  line with libOTPLess.a. If it does not, press the + button and
                  select it manually. Make sure that the Required status is
                  selected.
                </p>
                <p className="mr-t-20">
                  After that, go to the Build Phases tab, expand Link Binary
                  with Libraries, and make sure that it contains a line with
                  libOTPLess.a. If it does not, again, add it manually and set
                  it to required.
                </p>
                <p className="mr-t-20">
                  Finally, we’ve reached the last, and maybe the most sensitive
                  integration step — setting paths. It’s a step where it’s very
                  easy to make a mistake, so be neat and careful.
                </p>
                <p className="mr-t-20">In the Build Settings</p>
                <ul className="dots mr-t-10">
                  <li>tab, select your target</li>
                  <li>select All</li>
                  <li>and Combined</li>
                  <li>In the search field</li>
                  <li>
                    in the top-right corner, type Search Paths. Copy the path
                    from Library Search Path
                  </li>
                  <li>and paste it to Import Paths</li>
                </ul>
                <p className="mr-t-20">
                  If there is no value in Library Search Path, add a new one,
                  $(PROJECT_DIR)/lib.
                </p>
                <p className="mr-t-20">Do the same for Import Paths.</p>
                <p className="mr-t-20">
                  Now , the next step is to Register URL info in the targets
                  &#62; info &#62; URL Types
                </p>
                <p className="mr-t-10">identifier:- bundle identifier</p>
                <p className="mr-t-10">URL Scheme :- Your App Name</p>
                <Image
                  src={iOSSdkImg3}
                  className="mr-v-20 w-100 pd-20 br-9 bg-gray"
                />
                <p>
                  Now , the next step is to register our URL types in info.plist
                  file as shown above .
                </p>
                <ul className="dots mr-t-10">
                  <li>Open info.plist file</li>
                  <li>Add New property as URL Types of type Array</li>
                </ul>
                <p className="mr-t-20">
                  Adding some instructions for URL scheme
                </p>
                <Image
                  src={iOSSdkImg4}
                  className="mr-v-20 w-100 pd-20 br-9 bg-gray"
                />
                <p>
                  Now , if we check our applications info.plist file it should
                  have properties/ information declared like above .
                </p>
              </div>
            </>
          }
          titleClass="fs-24 font-heavy mr-t-20"
        />
      </div>
    );

    const Usage = (
      <div ref={iOSUsageRef}>
        <TitleBlock
          title={docsConstants.TITLES.IOS.USAGE}
          content={
            <>
              <p className="mr-t-20 fs-16">
                Then, go to your ViewController class in your custom application
                and add the code there:
              </p>
              <p className="mr-t-20 fs-16">
                (ii). In viewDidLoad method , set delegates:-
              </p>
              <p className="fs-14 mr-t-20 endpoint clr-white br-9">
                WhatsAppHandler.sharedInstance.initialiseOTPLess(delegate: self)
              </p>
              <p className="mr-t-20 fs-16">
                Let’s discuss this small piece of code.
              </p>
              <ul className="dots fs-16">
                <li>
                  <p>
                    Import OTPLess library. It will give you the ability to use
                    the code from your library in ViewController.
                  </p>
                  <p className="fs-14 mr-t-10 endpoint clr-white br-9">
                    Import OTPLess
                  </p>
                </li>
                <li>
                  <p>
                    Used whatsappHandler via shared instance to access our
                    desired function
                  </p>
                  <p className="fs-14 mr-t-10 endpoint clr-white br-9">
                    WhatsAppHandler.sharedInstance.initiateWhatsappLogin(scheme:
                    "https://wa.me/917878787878/?text=Sending verification
                    Message")
                  </p>
                  <p className="mr-t-20">
                    Here we have two Delegates for accessing the response code
                    and response String for the backend
                  </p>
                  <ul className="dots">
                    <li>
                      onSuccess. :- gives the response code and response if the
                      request is successful
                    </li>
                    <li>
                      onError. :- gives the response code and response if the
                      request is denied or some error is observed.
                    </li>
                  </ul>
                </li>
                <li>
                  You can also make a call for a method that belongs to the
                  library and save the result to a local variable. For sure, we
                  send some dummy data and receive a dummy response here.
                </li>
                <li>Print the result to the console.</li>
              </ul>
            </>
          }
          titleClass="fs-24 font-heavy mr-t-40 pd-t-40 dashed-border-top"
        />
      </div>
    );

    const HowItWorks = (
      <div ref={iOSHowItWorksRef}>
        <TitleBlock
          title={docsConstants.TITLES.IOS.HOW_IT_WORKS}
          content={
            <>
              <p className="mr-t-20 fs-16">
                The OTPLess has two functions namely as follows:-
              </p>
              <ul className="dots fs-16">
                <li>
                  <p>
                    This function helps in initialising the WhatsApp Chat with
                    the custom message pre-filled data.
                  </p>
                  <p className="fs-14 mr-t-10 endpoint clr-white br-9">
                    initiateWhatsappLogin(scheme : String) {}
                  </p>
                  <p className="mr-t-20">
                    <span className="font-heavy">Usage</span> :- This function
                    can be called overall in the application project with the
                    help of shared instance.
                  </p>
                </li>
                <li>
                  <p>
                    This function helps in getting the token from the DeepLink
                    URL which we can use for the successful login .
                  </p>
                  <p className="fs-14 mr-t-10 endpoint clr-white br-9">
                    parseToken(url: String) &#123;&#125;
                  </p>
                  <p className="mr-t-20">
                    <span className="font-heavy">Usage</span> :- This function
                    can be called in the SceneDelagete or AppDelegate depending
                    upon the version of the application. We can use this
                    function by providing it the URL and the parameter which we
                    are querying.The return String will be the your parameter
                    you just passed .
                  </p>
                </li>
              </ul>
              <div className="api">
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-16">
                      <p className="font-heavy">
                        When used in iOS version 13 and above :-
                      </p>
                      <p className="mr-t-20">For Use in SceneDelegate File:-</p>
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Below iOS version 13"}
                      codeToBeCopied={`
  func scene(_ scene:UIScene, openURLContexts URLContexts:Set<UIOpenURLContext>) {

    if let newUrl = URLContexts.first?.url.absoluteString {
      WhatsAppHandler.sharedInstance.parseToken(url:newUrl)
    }

  }
                    `}
                      code={
                        <>
                          <p>
                            <span className="attr-name">func</span>
                            <span className="attr-value mr-h-5">scene</span>
                            <span className="tag">(</span>
                            <span className="attr-name mr-h-5">_ scene</span>
                            <span className="tag">:</span>
                            <span className="attr-value mr-h-5">UIScene,</span>
                            <span className="attr-name">
                              <span className="punctuation">
                                {" "}
                                openURLContexts
                              </span>{" "}
                              URLContexts
                            </span>
                            <span className="tag">:</span>
                            <span className="attr-value mr-h-5">Set</span>
                            <span className="tag">&#60;</span>
                            <span className="attr-value">UIOpenURLContext</span>
                            <span className="tag">&#62;</span>
                            <span className="tag mr-h-5">)</span>
                            <span className="tag">&#123;</span>
                          </p>
                          <p className="mr-l-20">
                            <span className="attr-name">if</span>
                            <span className="punctuation mr-h-5">let</span>
                            <span className="attr-value">newUrl</span>
                            <span className="tag mr-h-5">=</span>
                            <span className="attr-name">URLContexts</span>.
                            <span className="attr-value">first</span>?.
                            <span className="attr-value">url</span>.
                            <span className="attr-value">absoluteString</span>
                            <span className="tag mr-h-5">&#123;</span>
                          </p>
                          <p className="mr-l-40">
                            <span className="attr-name">WhatsAppHandler</span>.
                            <span className="attr-value">sharedInstance</span>.
                            <span className="attr-value">parseToken</span>
                            <span className="tag">(</span>
                            <span className="attr-name mr-h-5">url</span>
                            <span className="tag">:</span>
                            <span className="attr-value mr-h-5">newUrl</span>
                            <span className="tag">)</span>
                          </p>
                          <p className="tag mr-t-5 mr-l-20">&#125;</p>
                          <p className="tag mr-t-5">&#125;</p>
                        </>
                      }
                    />
                  }
                  className={"mr-t-40"}
                />
                <DocsFlexSectionBlock
                  leftSection={
                    <div className="fs-16">
                      <p className="font-heavy">
                        When used below iOS version 13:-
                      </p>
                      <p className="mr-t-20">For Use in AppDelegate File:-</p>
                    </div>
                  }
                  rightSection={
                    <DocsCodeBlock
                      title={"Version 13 and above"}
                      codeToBeCopied={`
  func application (_ app:UIApplication, open url:URL, options:[UIApplication.OpenURLOptionsKey:Any]=[:]) -> Bool {

    if let resultantTokenDict = WhatsAppHandler.sharedInstance.parseToken(url:customURLRecieved) as String {
      print(resultantTokenDict)
    }

  }
                    `}
                      code={
                        <>
                          <p>
                            <span className="attr-name">func</span>
                            <span className="attr-value mr-h-5">
                              application
                            </span>
                            <span className="tag">(</span>
                            <span className="attr-name mr-h-5">_ app</span>
                            <span className="tag">:</span>
                            <span className="attr-value mr-h-5">
                              UIApplication,
                            </span>
                            <span className="attr-name">
                              <span className="punctuation"> open</span> url
                            </span>
                            <span className="tag">:</span>
                            <span className="attr-value mr-h-5">URL,</span>
                            <span className="attr-name mr-h-5">options</span>
                            <span className="tag">:</span>
                            <span className="tag mr-l-5">[</span>
                            <span className="attr-name">UIApplication</span>.
                            <span className="attr-value">
                              OpenURLOptionsKey
                            </span>
                            <span className="tag mr-h-5">:</span>
                            <span className="attr-value">Any</span>
                            <span className="tag mr-l-5">]</span>
                            <span className="tag mr-h-5">=</span>
                            <span className="tag">[</span>
                            <span className="punctuation">:</span>
                            <span className="tag">]</span>
                            <span className="tag mr-h-5">)</span>
                            <span className="tag">-&#62;</span>
                            <span className="punctuation mr-h-5">Bool</span>
                            <span className="tag">&#123;</span>
                          </p>
                          <p className="mr-l-20 mr-t-5">
                            <span className="attr-name">if</span>
                            <span className="punctuation mr-h-5">let</span>
                            <span className="attr-value">
                              resultantTokenDict
                            </span>
                            <span className="tag mr-h-5">=</span>
                            <span className="attr-name">WhatsAppHandler</span>.
                            <span className="attr-value">sharedInstance</span>.
                            <span className="attr-value">parseToken</span>
                            <span className="tag mr-h-5">(</span>
                            <span className="attr-name">url</span>
                            <span className="tag mr-h-5">:</span>
                            <span className="attr-value">
                              customURLRecieved
                            </span>
                            <span className="tag mr-h-5">)</span>
                          </p>
                          <p className="mr-l-20 mr-t-5">
                            <span className="attr-name">as</span>
                            <span className="punctuation mr-h-5">String</span>
                            <span className="tag">&#123;</span>
                          </p>
                          <p className="mr-l-40">
                            <span className="attr-value">print</span>
                            <span className="tag mr-l-5">(</span>
                            <span className="attr-value mr-h-5">
                              resultantTokenDict
                            </span>
                            <span className="tag">)</span>
                          </p>
                          <p className="tag mr-t-5 mr-l-20">&#125;</p>
                          <p className="tag mr-t-5">&#125;</p>
                        </>
                      }
                    />
                  }
                  className={"mr-t-40"}
                />
              </div>
            </>
          }
          titleClass="fs-24 font-heavy mr-t-40 pd-t-40 dashed-border-top"
        />
      </div>
    );

    return (
      <TitleBlock
        title={""}
        content={
          <div className="rest-api-sdk">
            {Installation}
            {Usage}
            {HowItWorks}
          </div>
        }
        titleClass="fs-24 font-heavy"
        className="animated fadeInUp delay-01s ios-sdk border-top mr-b-60 pd-t-30"
      />
    );
  };

  //Return UI
  return (
    <DocsScreen
      className="docs-home-screen"
      hasSideMenu
      hasHeader
      screenAnimationClass="no-animation"
      tabSelectCallback={scrollToSpecificElement}
      scrolledToElement={{ ...scrolledToElement }}
    >
      {WelcomeSection}
      {iOSSdk()}
    </DocsScreen>
  );
};

export default DocsHome;
